/**
 * The obligations the firm owes itself.
 *
 * Some controls are neither a gate nor a cron: a person has to do them on a
 * cadence, and nothing in the system can tell it is due. Re-checking the
 * banking calendar against the CNBV's published one is the first — the rules
 * the generator encodes were right the day they were written, and the only
 * thing that catches an authority adding a day is a human looking once a year.
 *
 * Left to memory, an obligation like that survives exactly as long as the
 * person who remembers it. So the screen shows what is owed, what happens if it
 * is skipped, and it will not accept a sign-off that does not say what was
 * found — because in a year that sentence is the entire record that the check
 * happened at all.
 *
 * There is no button to postpone anything, on purpose.
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import remindersService, { type OperationalReminder } from '../../services/reminders.service';
import authService from '../../services/auth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import { t } from '../../i18n/language';
import HeldDepositsSection from './HeldDepositsSection';
import {
  MIN_FINDINGS,
  canSignOff,
  describeDue,
  dueState,
  isFindingReady,
  sortByUrgency,
  type DueState,
} from './reminders.helpers';

const TONE: Record<DueState, BadgeTone> = {
  overdue: 'danger',
  due_soon: 'warning',
  scheduled: 'neutral',
};

export default function OperationalReminders(): JSX.Element {
  const [rows, setRows] = useState<OperationalReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const maySign = canSignOff(authService.getRole());

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await remindersService.list());
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load the operational checks.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const ordered = sortByUrgency(rows);
  const lapsed = ordered.filter((r) => dueState(r) === 'overdue').length;

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Operational checks"
        subtitle="Recurring work no job can do for us. Each one moves to its next date only when someone records what they found."
      />

      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm" role="alert"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {error}
        </div>
      )}
      {flash && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.success }}>
          {flash}
        </div>
      )}

      <HeldDepositsSection />

      {lapsed > 0 && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {lapsed} check{lapsed === 1 ? ' has' : 's have'} lapsed.
        </div>
      )}

      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}

      {!loading && ordered.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>Nothing is scheduled.</p>
      )}

      <ul className="space-y-3">
        {ordered.map((r) => (
          <ReminderCard
            key={r.id}
            reminder={r}
            maySign={maySign}
            onDone={(msg) => { setFlash(msg); void reload(); }}
            onError={setError}
          />
        ))}
      </ul>
    </div>
  );
}

function ReminderCard({ reminder, maySign, onDone, onError }: Readonly<{
  reminder: OperationalReminder;
  maySign: boolean;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const [open, setOpen] = useState(false);
  const [findings, setFindings] = useState('');
  const [busy, setBusy] = useState(false);
  const state = dueState(reminder);
  const ready = isFindingReady(findings) && !busy;

  const submit = async (): Promise<void> => {
    setBusy(true);
    try {
      await remindersService.complete(reminder.reminder_key, findings.trim());
      setOpen(false);
      setFindings('');
      onDone(`${reminder.title} — signed off. The next one is scheduled.`);
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not record the sign-off.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-2xl p-4"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold" style={{ color: colors.ink }}>{reminder.title}</div>
          <div className="text-xs mt-0.5" style={{ color: colors.muted }}>
            Every {reminder.cadence_months} month{reminder.cadence_months === 1 ? '' : 's'} ·
            next {reminder.next_due_on}
            {reminder.last_completed_at && reminder.last_completed_by
              && ` · last done ${formatDateTime(reminder.last_completed_at)} by ${reminder.last_completed_by}`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusBadge tone={TONE[state]}>{describeDue(reminder)}</StatusBadge>
          <button type="button" onClick={() => setOpen((v) => !v)}
                  className="text-xs px-3 py-1.5 rounded-md font-semibold"
                  style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
            {open ? 'Close' : t('Open', 'action')}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
          {/* Why it matters comes FIRST. Someone deciding whether this is worth
              their morning should decide on the real stakes, not on a title. */}
          <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>{reminder.why_it_matters}</p>

          <p className="text-xs font-semibold mb-1" style={{ color: colors.muted }}>WHAT TO DO</p>
          <pre className="text-xs whitespace-pre-wrap mb-4 p-3 rounded-lg"
               style={{ background: colors.canvas, color: colors.inkSoft, fontFamily: 'inherit' }}>
            {reminder.instructions}
          </pre>

          {reminder.last_findings && (
            <>
              <p className="text-xs font-semibold mb-1" style={{ color: colors.muted }}>
                LAST TIME
              </p>
              <p className="text-xs mb-4" style={{ color: colors.inkSoft }}>
                {reminder.last_findings}
              </p>
            </>
          )}

          {!maySign && (
            <p className="text-xs" style={{ color: colors.muted }}>
              A super admin signs this off.
            </p>
          )}

          {maySign && (
            <>
              <label htmlFor={`findings-${reminder.id}`} className="block text-xs font-semibold mb-1"
                     style={{ color: colors.inkSoft }}>
                What did you find?
              </label>
              <textarea
                id={`findings-${reminder.id}`}
                rows={3}
                value={findings}
                onChange={(e) => setFindings(e.target.value.slice(0, 4000))}
                placeholder="Compared the CNBV 2028 calendar against the generated list: no change."
                disabled={busy}
                className="w-full px-3 py-2 rounded-lg text-sm mb-1"
                style={{ border: `1px solid ${colors.border}` }}
              />
              <p className="text-xs mb-3" style={{ color: colors.muted }}>
                “No change” counts. “Done” does not — in a year this sentence is the whole
                record that the check happened. At least {MIN_FINDINGS} characters.
              </p>
              <button type="button" disabled={!ready} onClick={() => { void submit(); }}
                      className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                      style={{ background: colors.navy, color: '#FFFFFF' }}>
                {busy ? 'Recording…' : 'Sign it off'}
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}
